<template>
    <div class="kgr-row">
        <div class="kgr-float kgr-float--clear kgr-row">
            <div class="kgr-float kgr-float--left kgr-float--inText">
                <div class="kgr-inputGroup">
                    <div
                        v-t="'offering.customerId'"
                        class="kgr-inputGroup__label"
                    ></div>
                    <input
                        v-model="customerId"
                        type="text"
                        class="kgr-input"
                        @input="delayedSearch"
                    />
                </div>
            </div>
            <div class="kgr-float kgr-float--left kgr-float--inText">
                <div class="kgr-inputGroup">
                    <div
                        v-t="'offering.title'"
                        class="kgr-inputGroup__label"
                    ></div>
                    <input
                        v-model="title"
                        type="text"
                        class="kgr-input"
                        :placeholder="placeholderTitle"
                        size="50"
                        @input="delayedSearch"
                    />
                </div>
            </div>

            <div class="kgr-float kgr-float--left kgr-float--inText">
                <div class="kgr-inputGroup">
                    <div
                        v-t="'offering.sports'"
                        class="kgr-inputGroup__label"
                    ></div>
                    <kgr-multi-select
                        v-model="selectedSports"
                        :options="filteredSports"
                        searchable
                        @post-input="setPreFilter"
                    />
                </div>
            </div>
            <div class="kgr-float kgr-float--left kgr-float--inText">
                <div class="kgr-inputGroup">
                    <div
                        v-t="'offering.offeringCategory'"
                        class="kgr-inputGroup__label"
                    ></div>
                    <kgr-multi-select
                        v-model="selectedOfferingCategories"
                        :options="offeringCategoryOptions"
                        :inline="false"
                        searchable
                        @post-input="setPreFilter"
                    />
                </div>
            </div>
        </div>

        <div class="kgr-grid kgr-grid--full kgr-grid--medium-1of3 kgr-grid--gutters kgr-row">
            <div class="kgr-grid__cell">
                <div class="kgr-inputGroup">
                    <div
                        v-t="'offering.location'"
                        class="kgr-inputGroup__label"
                    ></div>
                    <kgr-multi-select
                        v-model="selectedLocations"
                        :options="filteredLocations"
                        :inline="false"
                        searchable
                        @post-input="setLocation"
                    />
                </div>
            </div>
            <div class="kgr-grid__cell">
                <div
                    v-if="selectedLocations.length > 0 || selectedAreas.length > 0"
                    class="kgr-inputGroup"
                >
                    <div
                        v-t="'offering.areas'"
                        class="kgr-inputGroup__label"
                    ></div>
                    <kgr-multi-select
                        v-model="selectedAreas"
                        display="label"
                        :options="filteredAreas"
                        :inline="false"
                        searchable
                        @post-input="setArea"
                    />
                </div>
            </div>
            <div class="kgr-grid__cell">
                <div
                    v-if="selectedAreas.length > 0 || selectedFields.length > 0"
                    class="kgr-inputGroup"
                >
                    <div
                        v-t="'offering.fields'"
                        class="kgr-inputGroup__label"
                    ></div>
                    <kgr-multi-select
                        v-model="selectedFields"
                        :options="filteredFields"
                        :inline="false"
                        display="label"
                        searchable
                        @post-input="setPreFilter"
                    />
                </div>
            </div>
        </div>

        <div class="kgr-grid kgr-grid--full kgr-grid--medium-1of6 kgr-grid--gutters kgr-row">
            <div class="kgr-grid__cell">
                <kgr-input-group
                    :label="$t('Termine ab dem')"
                    :error-message="error.messageForField('start')"
                >
                    <kgr-date-object-picker @change="filterWithStartDate" :value="filterStartDate" />
                </kgr-input-group>
            </div>
            <div class="kgr-grid__cell">
                <kgr-input-group
                    :label="$t('Termine bis zum')"
                    :error-message="error.messageForField('end')"
                >
                    <kgr-date-object-picker @change="filterWithEndDate" :value="filterEndDate" :maxDate="maxEnd" />
                </kgr-input-group>
            </div>
        </div>

        <div class="kgr-row">
            <kgr-accordion>
                <kgr-accordion-item
                    :title="`${$t('offering.moreFilters')} (${getMoreFiltersCount})`"
                    overflow
                >
                    <div class="kgr-grid kgr-grid--1of2 kgr-grid--large-1of3 kgr-grid--gutters">
                        <div class="kgr-grid__cell">
                            <div class="kgr-inputGroup">
                                <div
                                    v-t="'offering.weekday'"
                                    class="kgr-inputGroup__label"
                                ></div>
                                <kgr-weekday-multiselect
                                    v-model="selectedWeekdays"
                                    :inline="false"
                                    @post-input="setPreFilter"
                                />
                            </div>
                        </div>
                        <div class="kgr-grid__cell">
                            <kgr-multi-select
                                v-model="selectedDepartments"
                                :options="filteredDepartments"
                                :disabled="filterWithoutDepartment"
                                :label="$t('Abteilungen')"
                                :inline="false"
                                searchable
                                @post-input="setPreFilter"
                            />
                            <kgr-checkbox
                                :checked="filterWithoutDepartment"
                                :label="$t('offering.filterWithoutDepartment')"
                                @change="filterWithoutDepartmentChanged()"
                            />
                        </div>
                        <div class="kgr-grid__cell">
                            <kgr-multi-select
                                v-model="selectedTeams"
                                :options="filteredTeams"
                                :disabled="filterWithoutTeam"
                                :label="$t('Teams')"
                                :inline="false"
                                searchable
                                @post-input="setPreFilter"
                            />
                            <kgr-checkbox
                                :checked="filterWithoutTeam"
                                :label="$t('offering.filterWithoutTeam')"
                                @change="filterWithoutTeamChanged()"
                            />
                        </div>
                    </div>
                    <div class="kgr-separator kgr-hidden--preserveSpace"></div>
                    <div class="kgr-grid kgr-grid--1of2 kgr-grid--large-1of2 kgr-grid--gutters">
                        <div class="kgr-grid__cell">
                            <div class="kgr-inputGroup">
                                <div
                                    v-t="'offering.executionType'"
                                    class="kgr-inputGroup__label"
                                ></div>
                                <kgr-radio
                                    v-model="executionType"
                                    :options="executionTypes"
                                    :inline="true"
                                    @post-input="setPreFilter"
                                />
                            </div>
                        </div>
                        <div class="kgr-grid__cell">
                            <div class="kgr-inputGroup">
                                <div
                                    v-t="'offering.costs'"
                                    class="kgr-inputGroup__label"
                                ></div>
                                <kgr-radio
                                    v-model="selectedCost"
                                    :options="costFilterOptions"
                                    :inline="true"
                                    @post-input="setPreFilter"
                                />
                            </div>
                        </div>
                    </div>
                </kgr-accordion-item>
            </kgr-accordion>
        </div>

        <br />
        <div style="clear: both"></div>
        <br />
        <div>
            <kgr-loading v-if="loading" />

            <div v-else>
                <div class="kgr-postfix kgr-postfix--gutters kgr-postfix--center kgr-row">
                    <div class="kgr-postfix__fixed">
                        <kgr-paginator
                            v-if="meta.pagination.total > meta.pagination.count"
                            v-model="pagination"
                            :length="meta.pagination.total"
                            :show-limit-options="false"
                            show-pages
                            @input="findAllAndGetMeta"
                        />
                        <div v-else class="kgr-badge kgr-badge--info">
                            {{ meta && meta.pagination.total > 0 ? meta.pagination.total : '0' }} {{
                                $t('offering.hits')
                            }}
                        </div>
                    </div>
                    <div class="kgr-postfix__fluid"></div>
                    <div class="kgr-postfix__fixed">
                        <div class="kgr-inputGroup kgr-float--right">
                            <div
                                v-t="'offering.perpage'"
                                class="kgr-inputGroup__label"
                            ></div>
                            <kgr-generic-select
                                v-model="perPage"
                                :options="perPageSelect"
                                display="name"
                                inline
                                @post-input="updateMetaData"
                            />
                        </div>
                    </div>
                    <div
                        class="kgr-postfix__fixed"
                    >
                        <kgr-button
                            :pending="excelDownloadPending"
                            class="excel-button"
                            @click="downloadExcel()"
                        >
                            {{ $t('Excel-Download') }}
                        </kgr-button>
                    </div>
                </div>
                <div class="kgr-row">
                    <kgr-club-employee-trainer-appointments-list
                        :club-id="clubId"
                        :trainer-appointments="trainerAppointments"
                        :sort-field="sortField"
                        :sort-direction="sortDirection"
                        @sort="onSortChange"
                    />
                </div>
                <div class="kgr-postfix kgr-postfix--gutters kgr-postfix--center kgr-row">
                    <div class="kgr-postfix__fixed ">
                        <kgr-paginator
                            v-if="meta.pagination.total > meta.pagination.count"
                            v-model="pagination"
                            :length="meta.pagination.total"
                            :show-limit-options="false"
                            show-pages
                            @input="findAllAndGetMeta"
                        />
                        <div v-else class="kgr-badge kgr-badge--info">
                            {{ meta && meta.pagination.total > 0 ? meta.pagination.total : '0' }} {{ $t('offering.hits') }}
                        </div>
                    </div>
                    <div class="kgr-postfix__fluid"></div>
                    <div class="kgr-postfix__fixed">
                        <div class="kgr-inputGroup kgr-float--right">
                            <div
                                v-t="'offering.perpage'"
                                class="kgr-inputGroup__label"
                            ></div>
                            <kgr-generic-select
                                v-model="perPage"
                                :options="perPageSelect"
                                display="name"
                                inline
                                @post-input="updateMetaData"
                            />
                        </div>
                    </div>
                    <div
                        class="kgr-postfix__fixed"
                    >
                        <kgr-button
                            :pending="excelDownloadPending"
                            class="excel-button"
                            @click="downloadExcel()"
                        >
                            {{ $t('Excel-Download') }}
                        </kgr-button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Accordion from '../../components/common/accordion/Accordion';
import AccordionItem from '../../components/common/accordion/AccordionItem';
import Checkbox from '../../components/common/form/checkbox/Checkbox';
import ClubEmployeeTrainerAppointmentsList
    from '@legacy/components/club/employee/ClubEmployeeTrainerAppointmentsList.vue';
import DateObjectPicker from '../../components/common/form/picker/DateObjectPicker.vue';
import DepartmentRepository from '../../js/repository/club/offering/OfferingDepartmentRepository';
import Error from '@legacy/js/model/Error.js';
import GenericRadio from '../../components/common/form/radio/GenericRadio';
import GenericSelect from '../../components/common/form/select/GenericSelect';
import InputGroup from '../../components/common/form/InputGroup.vue';
import KgrButton from '../../components/common/form/button/KgrButton.vue';
import KgrDate from '../../js/date/Date.js';
import Loading from '../../components/common/Loading';
import LocationRepository from '../../js/repository/LocationRepository';
import MultiSelect from '../../components/common/form/select/MultiSelect';
import Offering from '../../js/dto/offering/Offering';
import OfferingCategoryRepository from '../../js/repository/club/offering/OfferingCategoryRepository';
import Paginator from '@legacy/components/common/Paginator.vue';
import SportsRepository from '../../js/repository/SportsRepository';
import TeamRepository from '../../js/repository/club/offering/OfferingTeamRepository';
import TrainerAppointmentRepository from '../../js/repository/club/offering/TrainerAppointmentRepository.js';
import WeekdayMultiSelect from '../../components/common/form/select/WeekdayMultiSelect';

const DEFAULT_CATEGORY_COURSE = 'course';
const DEFAULT_CATEGORY_EXAM = 'exam';
const DEFAULT_CATEGORY_TRAINING = 'training';
const DEFAULT_CATEGORIES = [DEFAULT_CATEGORY_COURSE, DEFAULT_CATEGORY_EXAM, DEFAULT_CATEGORY_TRAINING];

export default {
    components: {
        'kgr-button': KgrButton,
        'kgr-loading': Loading,
        'kgr-checkbox': Checkbox,
        'kgr-club-employee-trainer-appointments-list': ClubEmployeeTrainerAppointmentsList,
        'kgr-weekday-multiselect': WeekdayMultiSelect,
        'kgr-generic-select': GenericSelect,
        'kgr-multi-select': MultiSelect,
        'kgr-accordion': Accordion,
        'kgr-accordion-item': AccordionItem,
        'kgr-radio': GenericRadio,
        'kgr-date-object-picker': DateObjectPicker,
        'kgr-input-group': InputGroup,
        'kgr-paginator': Paginator,
    },
    props: {
        clubId: {
            type: Number,
            required: true,
        },
        employeeId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            maxEnd: KgrDate.today(),
            error: new Error(),
            sortField: 'appointment.start',
            sortDirection: 'asc',
            loading: false,
            trainerAppointments: [],
            selectedWeekdays: [],
            selectedSports: [],
            selectedLocations: [],
            fields: [],
            selectedTempLocations: [],
            selectedAreas: [],
            selectedTempAreas: [],
            selectedFields: [],
            selectedDepartments: [],
            selectedTeams: [],
            sportslist: [],
            fullLocations: [],
            fullAreas: [],
            departmentlist: [],
            teamlist: [],
            meta: null,
            title: null,
            customerId: null,
            executionType: null,
            selectedCost: null,
            placeholderTitle: this.$t('offering.pleaseFillIn'),
            locations: [],
            offeringCategories: [],
            selectedOfferingCategories: [],
            selectedStartDate: null,
            selectedEndDate: null,
            perPageSelect: [
                { name: 5, id: 5 },
                { name: 20, id: 20 },
                { name: 50, id: 50 },
                { name: 100, id: 100 },
                { name: 250, id: 250 },
                { name: 500, id: 500 },
            ],
            executionTypes: {
                'offering.all': null,
                'offering.presence': Offering.EXECUTION_TYPE_PRESENCE,
                'offering.hybrid': Offering.EXECUTION_TYPE_HYBRID,
                'offering.online': Offering.EXECUTION_TYPE_ONLINE,
            },
            costFilterOptions: {
                'offering.all_prices': null,
                'offering.price_free': true,
                'offering.price_chargeable': false,
            },
            appointments: [],
            perPage: { name: 20, id: 20 },
            page: 1,
            filterWithoutTeam: false,
            filterWithoutDepartment: false,
            timeout: null,
            excelDownloadPending: false,
            filterStartDate: (() => {
                const now = new Date(); const firstPrev = new Date(now.getFullYear(), now.getMonth() - 1, 1); return new KgrDate(firstPrev.getFullYear(), firstPrev.getMonth() + 1, firstPrev.getDate());
            })(), // first day of last month
            filterEndDate: (() => {
                const now = new Date(); const lastPrev = new Date(now.getFullYear(), now.getMonth(), 0); return new KgrDate(lastPrev.getFullYear(), lastPrev.getMonth() + 1, lastPrev.getDate());
            })(), // last day of last month
        };
    },
    computed: {
        pagination() {
            return {
                offset: !this.meta ? 0 : (this.meta.pagination.current_page - 1) * this.meta.pagination.per_page,
                limit: this.perPage.id, // !meta.pagination ? this.perPage : this.meta.pagination.per_page,
            };
        },
        getMoreFiltersCount() {
            let count = 0;
            count += this.selectedWeekdays.length;
            count += this.selectedDepartments.length;
            count += this.selectedTeams.length;
            if (this.selectedCost != null) {
                count++;
            }
            if (this.executionType && this.executionType.length > 0) {
                count++;
            }
            return count;
        },
        filteredSports() {
            return this.sportslist.filter((sport) => {
                return !this.selectedSports.some((selectedSport) => selectedSport.value === sport.value);
            });
        },
        filteredLocations() {
            return this.locationsSelect.filter((location) => {
                return !this.selectedLocations.some((selectedLocation) => selectedLocation.value === location.value);
            });
        },
        filteredAreas() {
            const result = [];
            const locations = Array.from(this.fullLocations);
            this.selectedTempLocations.forEach((location) => {
                const fullLocation = locations.find((fullLocation) => fullLocation.id === location.value);
                fullLocation.areas.forEach((area) => {
                    if ((this.selectedAreas.findIndex((selectedArea) => selectedArea.value === area.id) === -1)) {
                        result.push({
                            label: `${area.hierarchyPath} / ${area.name}`,
                            value: area.id,
                            hierarchyPath: area.hierarchyPath,
                        },
                        );
                    }
                    this.fullAreas.push(area);
                });
            });
            return result;
        },
        filteredFields() {
            const result = [];
            const areas = Array.from(this.fullAreas);
            this.selectedTempAreas.forEach((area) => {
                const fullArea = areas.find((fullArea) => fullArea.id === area.value);
                fullArea.fields.forEach((field) => {
                    if ((this.selectedFields.findIndex((selectedField) => selectedField.value === field.id) === -1)) {
                        result.push({
                            label: `${field.hierarchyPath} / ${field.name}`,
                            value: field.id,
                            hierarchyPath: field.hierarchyPath,
                        },
                        );
                    }
                });
            });
            return result;
        },
        filteredDepartments() {
            return this.departmentlist.filter((department) => {
                return !this.selectedDepartments.some((selectedDepartment) => selectedDepartment.value === department.value);
            });
        },
        filteredTeams() {
            return this.teamlist.filter((team) => {
                return !this.selectedTeams.some((selectedTeam) => selectedTeam.value === team.value);
            });
        },
        locationsSelect() {
            return this.fullLocations.map((location) => {
                return { label: location.name, value: location.id };
            });
        },
        offeringCategoryOptions() {
            return this.offeringCategories.map((category) => {
                return {
                    value: category.id,
                    label: DEFAULT_CATEGORIES.includes(category.name) ? this.$t(category.name) : category.name,
                };
            });
        },
        inBookableTeamsFilter() {
            if (this.filterWithoutTeam) {
                return [];
            }
            return this.selectedTeams.map((team) => team.value);
        },
        inBookableDepartmentsFilter() {
            if (this.filterWithoutDepartment) {
                return [];
            }
            return this.selectedDepartments.map((department) => department.value);
        },
        inAreasFilter() {
            let areas = this.selectedAreas;
            this.selectedFields.forEach((field) => {
                areas = areas.filter((area) => area.label !== field.hierarchyPath);
            });
            return areas.map((area) => area.value);
        },
        inLocationsFilter() {
            let locations = this.selectedLocations;
            this.selectedAreas.forEach((area) => {
                locations = locations.filter((location) => location.label !== area.hierarchyPath);
            });
            return locations.map((location) => location.value);
        },
        filter() {
            return {
                onWeekdays: this.selectedWeekdays.map((weekday) => weekday.value),
                inSports: this.selectedSports.map((sport) => sport.value),
                inFields: this.selectedFields.map((field) => field.value),
                inAreas: this.inAreasFilter,
                inLocations: this.inLocationsFilter,
                inOfferingCategories: this.selectedOfferingCategories.map((category) => category.value),
                withInstructors: [this.employeeId],
                inBookableTeams: this.inBookableTeamsFilter,
                inBookableDepartments: this.inBookableDepartmentsFilter,
                withoutBookableTeams: this.filterWithoutTeam,
                withoutBookableDepartments: this.filterWithoutDepartment,
                executionTypes: this.executionType ? [this.executionType] : [],
                withTermInTitle: this.title,
                customerId: this.customerId,
                isFree: this.selectedCost,
                from: this.filterStartDate.toIso8601(),
                to: this.filterEndDate.toIso8601(),
            };
        },
    },
    created() {
        SportsRepository.findAll(this.clubId)
            .then((sports) => sports.forEach((sport) => this.sportslist.push({ label: sport.name, value: sport.id })))
            .catch(() => this.$notify.error('error.loadingData'));
        LocationRepository.findAllByClubId(this.clubId)
            .then((locations) => {
                this.fullLocations = locations;
            })
            .catch(() => this.$notify.error('error.loadingData'));
        DepartmentRepository.findAll(this.clubId, true)
            .then((departments) => {
                departments.sort((a, b) => (`${a.departmentName} / ${a.name}`).toLowerCase().localeCompare((`${b.departmentName} / ${b.name}`).toLowerCase()));
                departments.forEach((department) => {
                    this.departmentlist.push({
                        label: department.name,
                        value: department.id,
                    });
                    this.getChildDepartments(department);
                });
            })
            .catch(() => this.$notify.error('error.loadingData'));
        TeamRepository.findAll(this.clubId, true)
            .then((teams) => {
                teams.sort((a, b) => (`${a.departmentName} / ${a.name}`).toLowerCase().localeCompare((`${b.departmentName} / ${b.name}`).toLowerCase()));
                teams.forEach((team) => this.teamlist.push({
                    label: `${team.departmentName} / ${team.name}`,
                    value: team.id,
                }));
            })
            .catch(() => this.$notify.error('error.loadingData'));

        OfferingCategoryRepository.findAllWithDefaultsAndArchived(this.clubId)
            .then((categories) => this.offeringCategories = categories)
            .catch(() => this.$notify.error('error.loadingData'));

        this.findAllAndGetMeta();
    },
    methods: {
        filterWithoutDepartmentChanged() {
            this.filterWithoutDepartment = !this.filterWithoutDepartment;
            this.selectedDepartments = [];
            this.setPreFilter();
        },
        filterWithoutTeamChanged() {
            this.filterWithoutTeam = !this.filterWithoutTeam;
            this.selectedTeams = [];
            this.setPreFilter();
        },
        getChildDepartments(department) {
            department.childDepartments.sort((a, b) => ((a.name).toLowerCase() < (b.name).toLowerCase()) ? -1 : ((a.name).toLowerCase() > (b.name).toLowerCase()) ? 1 : 0);
            department.childDepartments.forEach((childDepartment) => {
                this.departmentlist.push({
                    label: `${childDepartment.hierarchyPath} / ${childDepartment.name}`,
                    value: childDepartment.id,
                });
                this.getChildDepartments(childDepartment);
            });
        },
        setLocation() {
            this.selectedTempLocations = this.selectedLocations;
            this.selectedAreas = this.selectedAreas.filter((area) => this.selectedLocations.map((l) => l.label).includes(area.hierarchyPath));
            this.setArea();
        },
        setArea() {
            this.selectedTempAreas = this.selectedAreas;
            this.selectedFields = this.selectedFields.filter((field) => this.selectedAreas.map((a) => a.label).includes(field.hierarchyPath));
            this.setPreFilter();
        },
        setPreFilter() {
            this.findAllAndGetMeta();
        },
        updateMetaData() {
            this.findAllAndGetMeta();
        },
        onSortChange(sort) {
            this.sortField = sort.field;
            this.sortDirection = sort.direction;
            this.findAllAndGetMeta();
        },
        validate() {
            let error = false;
            this.error = new Error();
            if (!this.filterStartDate) {
                this.error.add('start', this.$t('offering.error.chooseStartDate'));
                error = true;
            }
            if (!this.filterEndDate) {
                this.error.add('end', this.$t('offering.error.chooseEndDate'));
                error = true;
                return;
            }
            if (this.filterStartDate && this.filterEndDate && this.filterStartDate.isAfter(this.filterEndDate)) {
                this.error.add('end', this.$t('Das Enddatum darf nicht vor dem Startdatum liegen.'));
                error = true;
            }
            return !error;
        },
        findAllAndGetMeta(pager) {
            if (!this.validate()) {
                return;
            }
            this.pagination.offset = pager?.offset ?? 0;
            this.pagination.limit = pager?.limit ?? this.perPage.id;
            this.loading = true;
            const filter = this.filter;
            TrainerAppointmentRepository.findAllAndGetMeta(this.clubId, filter, this.pagination.offset, this.pagination.limit, this.sortField, this.sortDirection)
                .then((data) => {
                    this.meta = data.meta;
                    // this.trainerAppointments = data.data.map((data) => Offering.fromData(data));
                    this.trainerAppointments = data.data;
                })
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => this.loading = false);
        },
        delayedSearch() {
            if (this.timeout) {
                clearTimeout(this.timeout);
            }
            this.timeout = setTimeout(() => {
                this.setPreFilter();
            }, 1000);
        },
        downloadExcel() {
            this.excelDownloadPending = true;
            TrainerAppointmentRepository.downloadExcel(this.clubId, this.filter)
                .catch(() => {
                    this.$notify.error('error.loadingData');
                })
                .finally(() => {
                    this.excelDownloadPending = false;
                });
        },
        filterWithStartDate(date) {
            this.filterStartDate = date;
            this.setPreFilter();
        },
        filterWithEndDate(date) {
            this.filterEndDate = date;
            this.setPreFilter();
        },
    },
};
</script>

<style scoped lang="scss" rel="stylesheet/scss">
.excel-button {
    margin-top: 25px;
}
</style>
